import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewCompactRoundedIcon from '@mui/icons-material/ViewCompactRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import { SxProps, Theme, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

/** Single source of truth for list/grid layout options (icon, message). */
export const layoutConfig = {
  list: {
    icon: ViewListRoundedIcon,
    message: defineMessage({
      id: 'itemsViewLayout.list',
      defaultMessage: 'Lista',
    }),
  },
  gridWide: {
    icon: GridViewRoundedIcon,
    message: defineMessage({
      id: 'itemsViewLayout.gridWide',
      defaultMessage: 'Grilla amplia',
    }),
  },
  gridNormal: {
    icon: AppsRoundedIcon,
    message: defineMessage({
      id: 'itemsViewLayout.gridNormal',
      defaultMessage: 'Grilla normal',
    }),
  },
  gridCompact: {
    icon: ViewCompactRoundedIcon,
    message: defineMessage({
      id: 'itemsViewLayout.gridCompact',
      defaultMessage: 'Grilla compacta',
    }),
  },
} as const;

export type ItemsViewLayout = keyof typeof layoutConfig;
export const layouts = Object.keys(layoutConfig) as ItemsViewLayout[];

interface ItemsViewLayoutSwitchProps {
  /** Current layout value (from parent or store). */
  value: ItemsViewLayout;
  /** Called when user selects another layout. */
  onChange: (value: ItemsViewLayout) => void;
  /** Optional styles for the ToggleButtonGroup. */
  sx?: SxProps<Theme>;
  /** Optional ID for the ToggleButtonGroup. */
  id?: string;
}

/**
 * Button group to switch between list and grid layouts (wide / normal / compact).
 * Only supports the fixed set: list, gridWide, gridNormal, gridCompact.
 */
export function ItemsViewLayoutSwitch({ value, onChange, sx, id }: ItemsViewLayoutSwitchProps) {
  const { $t } = useIntl();
  const effectiveValue = layouts.includes(value) ? value : 'list';

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: ItemsViewLayout | null) => {
    if (newValue != null) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      value={effectiveValue}
      exclusive
      onChange={handleChange}
      size="small"
      sx={sx}
      id={id}
    >
      {layouts.map((layout) => {
        const config = layoutConfig[layout];
        const Icon = config.icon;
        return (
          <ToggleButton key={layout} value={layout}>
            <Tooltip title={$t(config.message)}>
              <Icon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
